/* Infinity Works Supply — global theme behaviour. Vanilla JS, no build step. */
(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');

  /* ---------------------------------------------------------------------
   * Scroll reveal animations
   * ------------------------------------------------------------------- */
  const revealTargets = document.querySelectorAll('.animate-in');
  if (revealTargets.length && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    revealTargets.forEach((el) => revealObserver.observe(el));
  } else {
    revealTargets.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------------------------------------------------------------------
   * Animated counters (Company Statistics section)
   * ------------------------------------------------------------------- */
  const counters = document.querySelectorAll('[data-counter-target]');
  if (counters.length && 'IntersectionObserver' in window) {
    const runCounter = (el) => {
      const target = parseFloat(el.dataset.counterTarget);
      const decimals = el.dataset.counterDecimals ? parseInt(el.dataset.counterDecimals, 10) : 0;
      const suffix = el.dataset.counterSuffix || '';
      const duration = 1600;
      const start = performance.now();

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = target * eased;
        el.textContent = value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + suffix;
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const counterObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            runCounter(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((el) => counterObserver.observe(el));
  }

  /* ---------------------------------------------------------------------
   * Sticky header shadow state
   * ------------------------------------------------------------------- */
  const header = document.querySelector('[data-site-header]');
  if (header) {
    const setScrolled = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    setScrolled();
    window.addEventListener('scroll', setScrolled, { passive: true });
  }

  /* ---------------------------------------------------------------------
   * Focus trap helper for drawers / modals
   * ------------------------------------------------------------------- */
  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first.focus();

    function handleKeydown(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    container.addEventListener('keydown', handleKeydown);
    container._releaseFocusTrap = () => container.removeEventListener('keydown', handleKeydown);
  }

  /* ---------------------------------------------------------------------
   * Mobile navigation drawer
   * ------------------------------------------------------------------- */
  class MobileDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.panel = this.querySelector('[data-drawer-panel]');
      document.querySelectorAll('[data-drawer-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.open());
      });
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      this.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const submenu = toggle.nextElementSibling;
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!isOpen));
          if (submenu) submenu.classList.toggle('is-open', !isOpen);
        });
      });
    }

    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.setAttribute('aria-hidden', 'false');
      if (this.panel) trapFocus(this.panel);
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      this.setAttribute('aria-hidden', 'true');
      if (this.panel && this.panel._releaseFocusTrap) this.panel._releaseFocusTrap();
    }
  }
  if (!customElements.get('mobile-drawer')) customElements.define('mobile-drawer', MobileDrawer);

  /* ---------------------------------------------------------------------
   * Predictive search
   * ------------------------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.resultsEl = this.querySelector('[data-search-results]');
      this.toggleBtn = document.querySelector('[data-search-open]');
      this.closeBtn = this.querySelector('[data-search-close]');
      this.debounceTimer = null;

      if (this.toggleBtn) this.toggleBtn.addEventListener('click', () => this.open());
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      if (this.input) {
        this.input.addEventListener('input', () => {
          clearTimeout(this.debounceTimer);
          const query = this.input.value.trim();
          if (query.length < 2) {
            this.resultsEl.innerHTML = '';
            return;
          }
          this.debounceTimer = setTimeout(() => this.fetchResults(query), 250);
        });
      }
    }

    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => this.input && this.input.focus());
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    fetchResults(query) {
      fetch(`${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`)
        .then((res) => res.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const results = doc.querySelector('[data-search-results]');
          this.resultsEl.innerHTML = results ? results.innerHTML : '';
        })
        .catch(() => {});
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------------------------------------------------------------------
   * Cart drawer + AJAX add-to-cart
   * ------------------------------------------------------------------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.querySelectorAll('[data-cart-open]').forEach((btn) => btn.addEventListener('click', () => this.open()));
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      this.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.updateItem(removeBtn.dataset.cartRemove, 0);
        }
        const qtyBtn = e.target.closest('[data-cart-qty]');
        if (qtyBtn) {
          e.preventDefault();
          const input = qtyBtn.parentElement.querySelector('input[type="number"]');
          const delta = qtyBtn.dataset.cartQty === 'plus' ? 1 : -1;
          const newQty = Math.max(0, parseInt(input.value, 10) + delta);
          this.updateItem(input.dataset.key, newQty);
        }
        const estimateBtn = e.target.closest('[data-estimator-submit]');
        if (estimateBtn) {
          e.preventDefault();
          this.estimateShipping();
        }
      });
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-cart-note]')) {
          this.updateNote(e.target.value);
        }
      });
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-coupon-input]')) {
          sessionStorage.setItem('iws_coupon', e.target.value.trim());
          this.syncCheckoutLink();
        }
      });

      this.restoreCoupon();
    }

    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this.panel) trapFocus(this.panel);
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this.panel && this.panel._releaseFocusTrap) this.panel._releaseFocusTrap();
    }

    refresh() {
      fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newContent = doc.querySelector('cart-drawer');
          if (newContent) this.innerHTML = newContent.innerHTML;
          this.restoreCoupon();
        })
        .then(() => updateCartCount());
    }

    updateItem(key, quantity) {
      fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      })
        .then((res) => res.json())
        .then(() => this.refresh());
    }

    updateNote(note) {
      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      }).catch((err) => console.error(err));
    }

    restoreCoupon() {
      const saved = sessionStorage.getItem('iws_coupon');
      const input = this.querySelector('[data-coupon-input]');
      if (saved && input) input.value = saved;
      this.syncCheckoutLink();
    }

    syncCheckoutLink() {
      const link = this.querySelector('[data-checkout-link]');
      if (!link) return;
      const code = sessionStorage.getItem('iws_coupon');
      link.href = code ? `/checkout?discount=${encodeURIComponent(code)}` : '/checkout';
    }

    estimateShipping() {
      const country = this.querySelector('[data-estimator-country]');
      const zip = this.querySelector('[data-estimator-zip]');
      const results = this.querySelector('[data-estimator-results]');
      if (!country || !zip || !results) return;

      results.textContent = 'Calculating…';
      const params = new URLSearchParams({
        'shipping_address[zip]': zip.value,
        'shipping_address[country]': country.value,
      });

      fetch(`${window.Shopify.routes.root}cart/shipping_rates.json?${params.toString()}`)
        .then((res) => res.json())
        .then((data) => {
          if (!data.shipping_rates || data.shipping_rates.length === 0) {
            results.textContent = 'No shipping rates available for this address.';
            return;
          }
          const format = window.IWSFormatMoney || ((cents) => `$${(cents / 100).toFixed(2)}`);
          results.innerHTML = data.shipping_rates
            .map((rate) => `<div class="rate"><span>${rate.name}</span><span>${format(Math.round(parseFloat(rate.price) * 100))}</span></div>`)
            .join('');
        })
        .catch(() => {
          results.textContent = 'Enter a valid postal code and country to see rates.';
        });
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  function updateCartCount() {
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('is-visible', cart.item_count > 0);
        });
      });
  }
  window.updateCartCount = updateCartCount;

  function addToCart(formData, triggerEl) {
    if (triggerEl) {
      triggerEl.classList.add('is-loading');
      triggerEl.disabled = true;
    }
    return fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(formData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          throw new Error(data.description || 'Unable to add to cart');
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          drawer.refresh();
          drawer.open();
        }
        if (triggerEl) {
          triggerEl.classList.add('is-added');
          setTimeout(() => triggerEl.classList.remove('is-added'), 1600);
        }
        return data;
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        if (triggerEl) {
          triggerEl.classList.remove('is-loading');
          triggerEl.disabled = false;
        }
      });
  }
  window.IWSAddToCart = addToCart;

  document.addEventListener('click', (e) => {
    const quickAddBtn = e.target.closest('[data-quick-add]');
    if (!quickAddBtn) return;
    e.preventDefault();
    addToCart({ id: quickAddBtn.dataset.variantId, quantity: 1 }, quickAddBtn);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());
    payload.quantity = payload.quantity || 1;
    const submitBtn = form.querySelector('[type="submit"]');
    addToCart(payload, submitBtn);
  });

  /* ---------------------------------------------------------------------
   * Wishlist (localStorage-backed, no account or app required)
   * ------------------------------------------------------------------- */
  const WISHLIST_KEY = 'iws_wishlist';

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  }

  function isWishlisted(id) {
    return getWishlist().some((item) => String(item.id) === String(id));
  }

  function toggleWishlistItem(data) {
    const items = getWishlist();
    const index = items.findIndex((item) => String(item.id) === String(data.id));
    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push(data);
    }
    saveWishlist(items);
    syncWishlistUI();
  }

  function removeWishlistItem(id) {
    saveWishlist(getWishlist().filter((item) => String(item.id) !== String(id)));
    syncWishlistUI();
  }

  function renderWishlistDrawer() {
    const drawer = document.querySelector('wishlist-drawer');
    const template = document.getElementById('WishlistItemTemplate');
    if (!drawer || !template) return;

    const list = drawer.querySelector('[data-wishlist-items]');
    const empty = drawer.querySelector('[data-wishlist-empty]');
    const items = getWishlist();

    list.innerHTML = '';

    if (items.length === 0) {
      list.hidden = true;
      empty.hidden = false;
      return;
    }

    list.hidden = false;
    empty.hidden = true;

    items.forEach((item) => {
      const node = template.content.cloneNode(true);
      node.querySelectorAll('[data-field="url"]').forEach((el) => {
        el.href = item.url;
      });
      const img = node.querySelector('[data-field="image"]');
      img.src = item.image;
      img.alt = item.title;
      node.querySelector('[data-field-text="title"]').textContent = item.title;
      node.querySelector('[data-field-text="price"]').textContent = item.price;

      node.querySelector('[data-wishlist-remove]').addEventListener('click', () => removeWishlistItem(item.id));

      const addBtn = node.querySelector('[data-wishlist-add]');
      addBtn.addEventListener('click', () => {
        if (!item.handle) {
          window.location.href = item.url;
          return;
        }
        fetch(`${window.Shopify.routes.root}products/${item.handle}.js`)
          .then((res) => res.json())
          .then((product) => {
            const variant = product.variants.find((v) => v.available) || product.variants[0];
            if (!variant) throw new Error('No purchasable variant');
            return addToCart({ id: variant.id, quantity: 1 }, addBtn);
          })
          .catch(() => {
            window.location.href = item.url;
          });
      });

      list.appendChild(node);
    });
  }

  function syncWishlistUI() {
    const items = getWishlist();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = items.length;
      el.classList.toggle('hidden', items.length === 0);
    });
    document.querySelectorAll('[data-wishlist-count-inline]').forEach((el) => {
      el.textContent = items.length;
    });
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const active = isWishlisted(btn.dataset.productId);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
    renderWishlistDrawer();
  }

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.querySelectorAll('[data-wishlist-open]').forEach((btn) => btn.addEventListener('click', () => this.open()));
      document.querySelectorAll('[data-wishlist-open]').forEach((btn) => btn.addEventListener('click', () => this.open()));
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this.panel) trapFocus(this.panel);
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this.panel && this.panel._releaseFocusTrap) this.panel._releaseFocusTrap();
    }
  }
  if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);

  document.addEventListener('click', (e) => {
    const wishlistBtn = e.target.closest('[data-wishlist-toggle]');
    if (!wishlistBtn) return;
    e.preventDefault();
    toggleWishlistItem({
      id: wishlistBtn.dataset.productId,
      handle: wishlistBtn.dataset.productHandle,
      title: wishlistBtn.dataset.productTitle,
      url: wishlistBtn.dataset.productUrl,
      image: wishlistBtn.dataset.productImage,
      price: wishlistBtn.dataset.productPrice,
    });
  });

  syncWishlistUI();

  /* ---------------------------------------------------------------------
   * Recently viewed strip (reads the localStorage snapshot written by
   * snippets/recently-viewed-tracker.liquid on product page views)
   * ------------------------------------------------------------------- */
  function renderRecentlyViewed() {
    const template = document.getElementById('RecentlyViewedTemplate');
    document.querySelectorAll('[data-recently-viewed]').forEach((container) => {
      const row = container.querySelector('[data-recently-viewed-row]');
      if (!row || !template) return;

      let items = [];
      try {
        items = JSON.parse(localStorage.getItem('iws_recently_viewed')) || [];
      } catch (e) {
        items = [];
      }

      const excludeId = container.dataset.excludeId;
      items = items.filter((item) => String(item.id) !== String(excludeId));

      if (items.length === 0) {
        container.hidden = true;
        return;
      }

      container.hidden = false;
      row.innerHTML = '';
      items.forEach((item) => {
        const node = template.content.cloneNode(true);
        node.querySelectorAll('[data-field="url"]').forEach((el) => {
          el.href = item.url;
        });
        const img = node.querySelector('[data-field="image"]');
        img.src = item.image;
        img.alt = item.title;
        node.querySelector('[data-field-text="title"]').textContent = item.title;
        node.querySelector('[data-field-text="price"]').textContent = item.price;
        row.appendChild(node);
      });
    });
  }
  renderRecentlyViewed();

  /* ---------------------------------------------------------------------
   * Quick view modal — fetches the lightweight `product.quick-view` alternate
   * template and injects its `.quick-view-content` markup, reusing the same
   * variant-selects / add-to-cart wiring as the full product page.
   * ------------------------------------------------------------------- */
  class QuickViewModal extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.contentEl = this.querySelector('[data-quick-view-content]');
      this.loadingEl = this.querySelector('[data-quick-view-loading]');
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-view]');
        if (!trigger) return;
        e.preventDefault();
        this.load(trigger.dataset.productUrl);
      });
    }

    load(url) {
      this.contentEl.innerHTML = '';
      this.loadingEl.hidden = false;
      this.open();

      fetch(`${url}?view=quick-view`)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('.quick-view-content');
          if (content) {
            this.contentEl.appendChild(content);
            syncWishlistUI();
          } else {
            this.contentEl.innerHTML = '<p class="quick-view-modal__error">Sorry, this product couldn’t be loaded.</p>';
          }
        })
        .catch(() => {
          this.contentEl.innerHTML = '<p class="quick-view-modal__error">Sorry, this product couldn’t be loaded.</p>';
        })
        .finally(() => {
          this.loadingEl.hidden = true;
        });
    }

    open() {
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this.panel) trapFocus(this.panel);
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this.panel && this.panel._releaseFocusTrap) this.panel._releaseFocusTrap();
    }
  }
  if (!customElements.get('quick-view-modal')) customElements.define('quick-view-modal', QuickViewModal);

  /* ---------------------------------------------------------------------
   * Newsletter form feedback
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
    form.addEventListener('submit', () => {
      const btn = form.querySelector('[type="submit"]');
      if (btn) btn.classList.add('is-loading');
    });
  });
})();
